import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Calendar, Plus, Search, Eye, Trash2, UserPlus, Users, CheckCircle, 
  XCircle, Clock, Settings, ChevronRight, AlertCircle, User, Download,
  Home, Building2, FileText, RotateCcw, X, MapPin
} from 'lucide-react';
import EventCardDesigner from '../components/EventCardDesigner';
import EventRegistrationsList from '../components/EventRegistrationsList';

export default function EventsPage() {
  const { api, user, canExportExcel } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');
  
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const hasEventAccess = user?.event_access?.length > 0;
  const isEventIncharge = user?.roles?.includes('event_incharge') || user?.role === 'event_incharge';
  const isKhidmatGuzar = user?.role === 'mumineen' && hasEventAccess;

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/events?status=${statusFilter}`);
      let fetchedEvents = response.data.events || [];
      
      // For non-admin users with event access, filter to only show assigned events
      // This includes Khidmat Guzars, Event Incharges, and Mohalla Heads
      if (!isAdmin && hasEventAccess) {
        const assignedEventIds = user.event_access.map(e => e.event_id);
        console.log('Event access filtering - assigned events:', assignedEventIds);
        console.log('All events:', fetchedEvents.map(e => e.id));
        fetchedEvents = fetchedEvents.filter(e => assignedEventIds.includes(e.id));
        console.log('Filtered events:', fetchedEvents.length);
      }
      
      setEvents(fetchedEvents);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      toast.error('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter, isAdmin, hasEventAccess, user?.event_access]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await api.delete(`/events/${eventId}`);
      toast.success('Event deleted');
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    }
  };

  const handleCompleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to mark this event as completed? This action cannot be undone.')) return;
    try {
      await api.post(`/events/${eventId}/complete`);
      toast.success('Event marked as completed');
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete event');
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-navy">
            {isKhidmatGuzar && !isAdmin ? 'My Distribution' : 'Event Management'}
          </h1>
          <p className="text-xs sm:text-sm text-slate-500">
            {isKhidmatGuzar && !isAdmin 
              ? 'Mark distribution status' 
              : 'Manage events & assignments'}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowCreateDialog(true)} className="bg-navy hover:bg-navy/90 text-sm" size="sm">
            <Plus className="w-4 h-4 mr-2" />
            Create Event
          </Button>
        )}
      </div>

      {/* Filter Tabs */}
      {/* For Khidmat Guzar - no status filter tabs */}
      {isAdmin && (
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="active" className="flex-1 sm:flex-none flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Active Events</span>
              <span className="sm:hidden">Active</span>
            </TabsTrigger>
            <TabsTrigger value="completed" className="flex-1 sm:flex-none flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Completed</span>
              <span className="sm:hidden">Done</span>
            </TabsTrigger>
            <TabsTrigger value="all" className="flex-1 sm:flex-none text-xs sm:text-sm">All</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {/* Events Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-2 border-navy border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : events.length === 0 ? (
        <Card className="border-0 shadow-card">
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">
              {isKhidmatGuzar && !isAdmin 
                ? 'You are not assigned to any active events' 
                : `No ${statusFilter !== 'all' ? statusFilter : ''} events found`}
            </p>
            {isAdmin && (
              <Button onClick={() => setShowCreateDialog(true)} className="mt-4 bg-navy">
                <Plus className="w-4 h-4 mr-2" />
                Create First Event
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map(event => {
            // For Khidmat Guzar, get their assigned sectors for this event
            const userEventAccess = user?.event_access?.find(e => e.event_id === event.id);
            const userAssignedSectors = userEventAccess?.sectors || [];
            const userRole = userEventAccess?.role || null;
            const userMohalla = userEventAccess?.mohalla || null;
            const isAlsoKG = userEventAccess?.is_also_kg || false;
            const kgSectors = userEventAccess?.kg_sectors || [];
            
            // Pure Khidmat Guzar (not also a Mohalla Head): Show SEPARATE CARD FOR EACH SECTOR
            if (userRole === 'khidmat_guzar' && !isAdmin && userAssignedSectors.length > 0) {
              return userAssignedSectors.map(sector => (
                <SectorCard 
                  key={`${event.id}-${sector}`}
                  event={event}
                  sector={sector}
                  sectorStats={event.sector_stats?.find(s => s.sector === sector)}
                  onView={() => setSelectedEvent({ ...event, userAssignedSectors, initialSector: sector })}
                />
              ));
            }
            
            // For Mohalla Head who is ALSO KG: Show EventCard + SectorCards for their KG sectors
            if (userRole === 'mohalla_head' && isAlsoKG && kgSectors.length > 0 && !isAdmin) {
              return (
                <React.Fragment key={event.id}>
                  {/* Main Mohalla Head card */}
                  <EventCard 
                    event={event} 
                    isAdmin={isAdmin}
                    isKhidmatGuzar={false}
                    isMohallaHead={true}
                    isEventIncharge={false}
                    isAlsoKG={isAlsoKG}
                    kgSectors={kgSectors}
                    userMohalla={userMohalla}
                    userAssignedSectors={userAssignedSectors}
                    onView={() => setSelectedEvent({ ...event, userAssignedSectors, kgSectors, isAlsoKG })}
                    onDelete={() => handleDeleteEvent(event.id)}
                    onComplete={() => handleCompleteEvent(event.id)}
                  />
                  {/* Individual sector cards for KG distribution */}
                  {kgSectors.map(sector => (
                    <SectorCard 
                      key={`${event.id}-kg-${sector}`}
                      event={event}
                      sector={sector}
                      sectorStats={event.sector_stats?.find(s => s.sector === sector)}
                      onView={() => setSelectedEvent({ ...event, userAssignedSectors: kgSectors, initialSector: sector })}
                    />
                  ))}
                </React.Fragment>
              );
            }
            
            // For all others (Admin, Event Incharge, Mohalla Head without KG): Show one card per event
            return (
              <EventCard 
                key={event.id} 
                event={event} 
                isAdmin={isAdmin}
                isKhidmatGuzar={userRole === 'khidmat_guzar'}
                isMohallaHead={userRole === 'mohalla_head'}
                isEventIncharge={userRole === 'incharge'}
                isAlsoKG={isAlsoKG}
                kgSectors={kgSectors}
                userMohalla={userMohalla}
                userAssignedSectors={userAssignedSectors}
                onView={() => setSelectedEvent({ ...event, userAssignedSectors, kgSectors, isAlsoKG })}
                onDelete={() => handleDeleteEvent(event.id)}
                onComplete={() => handleCompleteEvent(event.id)}
              />
            );
          })}
        </div>
      )}

      {/* Create Event Dialog */}
      {showCreateDialog && (
        <CreateEventDialog 
          api={api}
          onClose={() => setShowCreateDialog(false)}
          onSuccess={() => { fetchEvents(); setShowCreateDialog(false); }}
        />
      )}

      {/* Event Details Dialog */}
      {selectedEvent && (
        <EventDetailsDialog 
          event={selectedEvent}
          api={api}
          user={user}
          isAdmin={isAdmin}
          canExportExcel={canExportExcel}
          onClose={() => setSelectedEvent(null)}
          onRefresh={() => { fetchEvents(); }}
        />
      )}
    </div>
  );
}

// Sector Card Component - For Khidmat Guzar (one card per sector)
function SectorCard({ event, sector, sectorStats, onView }) {
  const stats = sectorStats || { total_families: 0, done: 0, not_done: 0, given_to_other: 0 };
  const delivered = (stats.done || 0) + (stats.given_to_other || 0);
  const processed = delivered + (stats.not_done || 0);
  const total = stats.total_families || 0;
  const progressPercent = total > 0 ? Math.round((processed / total) * 100) : 0;
  const isComplete = processed >= total && total > 0;
  
  return (
    <Card className={`border-0 shadow-card hover:shadow-lg transition-shadow ${isComplete ? 'ring-2 ring-emerald-500' : ''}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Badge className="bg-navy text-white">{sector}</Badge>
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">{event.name}</p>
            <p className="text-xs text-slate-400">
              {new Date(event.event_date).toLocaleDateString('en-IN', { 
                day: 'numeric', month: 'short', year: 'numeric' 
              })}
            </p>
          </div>
          {isComplete ? (
            <Badge className="bg-emerald-500">
              <CheckCircle className="w-3 h-3 mr-1" />
              Complete
            </Badge>
          ) : (
            <Badge className="bg-blue-500">Active</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress Stats */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-2xl font-bold text-navy">{total}</p>
            <p className="text-xs text-slate-500">Total Families</p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-3">
            <p className="text-2xl font-bold text-emerald-600">{delivered}</p>
            <p className="text-xs text-slate-500">Delivered</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-3">
            <p className="text-2xl font-bold text-orange-600">{stats.not_done || 0}</p>
            <p className="text-xs text-slate-500">Returns</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="font-medium text-slate-700">Progress</span>
            <span className={`font-bold ${isComplete ? 'text-emerald-600' : 'text-navy'}`}>
              {processed}/{total} ({progressPercent}%)
            </span>
          </div>
          <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-500 ${isComplete ? 'bg-emerald-500' : 'bg-blue-500'}`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* Pending Count */}
        {!isComplete && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-center">
            <p className="text-sm text-amber-700">
              <AlertCircle className="w-4 h-4 inline mr-1" />
              <strong>{total - processed}</strong> families pending
            </p>
          </div>
        )}

        {/* Action Button */}
        <Button 
          className={`w-full ${isComplete ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-navy hover:bg-navy/90'}`}
          onClick={onView}
        >
          {isComplete ? (
            <>
              <Eye className="w-4 h-4 mr-2" />
              View Details
            </>
          ) : (
            <>
              <ChevronRight className="w-4 h-4 mr-2" />
              Continue Distribution
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

// Event Card Component
function EventCard({ event, isAdmin, isKhidmatGuzar, isMohallaHead, isEventIncharge, isAlsoKG, kgSectors, userMohalla, userAssignedSectors, onView, onDelete, onComplete }) {
  const progress = event.progress || { total: 0, done: 0, processed: 0, not_done: 0 };
  
  // Get mohalla sectors from sector_stats
  const mohallaSectors = isMohallaHead && userMohalla 
    ? (event.sector_stats?.filter(s => {
        // Match sectors that belong to user's mohalla
        const sectorName = s.sector;
        // We need to check mohalla from sector data - for now use naming convention
        return event.mohalla_sectors?.[userMohalla]?.includes(sectorName);
      }) || [])
    : [];
  
  // For Mohalla Head, calculate progress from their mohalla's sectors
  let mohallaProgress = { total: 0, processed: 0, not_done: 0, sectors: 0, kg_assigned: 0 };
  if (isMohallaHead && userMohalla && event.sector_stats) {
    // Get sectors in user's mohalla
    const userMohallaSectors = event.sector_stats.filter(s => 
      event.mohalla_sector_map?.[s.sector] === userMohalla
    );
    mohallaProgress.sectors = userMohallaSectors.length;
    
    // Get KG assignments for mohalla sectors
    const mohallaSectorNames = userMohallaSectors.map(s => s.sector);
    const mohallaKGAssignments = (event.khidmat_assignments || []).filter(
      kg => mohallaSectorNames.includes(kg.sector)
    );
    mohallaProgress.kg_assigned = mohallaKGAssignments.length;
    
    userMohallaSectors.forEach(stat => {
      mohallaProgress.total += stat.total_families || 0;
      mohallaProgress.processed += (stat.done || 0) + (stat.not_done || 0);
      mohallaProgress.not_done += stat.not_done || 0;
    });
  }
  
  // For users who are also KG, calculate progress from their KG sectors
  let kgProgress = { total: 0, processed: 0, not_done: 0 };
  if (isAlsoKG && kgSectors?.length > 0 && event.sector_stats) {
    const kgSectorStats = event.sector_stats.filter(s => kgSectors.includes(s.sector));
    kgSectorStats.forEach(stat => {
      kgProgress.total += stat.total_families || 0;
      kgProgress.processed += (stat.done || 0) + (stat.not_done || 0);
      kgProgress.not_done += stat.not_done || 0;
    });
  }
  
  // For Khidmat Guzar, calculate progress from their assigned sectors' stats
  let userProgress = { total: 0, processed: 0, not_done: 0 };
  if (isKhidmatGuzar && userAssignedSectors?.length > 0 && event.sector_stats) {
    const userSectorStats = event.sector_stats?.filter(s => userAssignedSectors.includes(s.sector)) || [];
    userSectorStats.forEach(stat => {
      userProgress.total += stat.total_families || 0;
      userProgress.processed += (stat.done || 0) + (stat.not_done || 0);
      userProgress.not_done += stat.not_done || 0;
    });
  }
  
  // Use appropriate progress based on role
  const displayProgress = isKhidmatGuzar ? userProgress : (isMohallaHead && !isAdmin ? mohallaProgress : progress);
  const progressPercent = displayProgress.total > 0 ? Math.round((displayProgress.processed / displayProgress.total) * 100) : 0;
  const kgProgressPercent = kgProgress.total > 0 ? Math.round((kgProgress.processed / kgProgress.total) * 100) : 0;

  return (
    <Card className="border-0 shadow-card hover:shadow-lg transition-shadow">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{event.name}</CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              {new Date(event.event_date).toLocaleDateString('en-IN', { 
                day: 'numeric', month: 'short', year: 'numeric' 
              })}
            </p>
          </div>
          <Badge className={event.status === 'completed' ? 'bg-emerald-500' : 'bg-blue-500'}>
            {event.status === 'completed' ? 'Completed' : 'Active'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {event.description && (
          <p className="text-sm text-slate-600">{event.description}</p>
        )}

        {/* For Khidmat Guzar - Show their assigned sectors */}
        {isKhidmatGuzar && userAssignedSectors?.length > 0 && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <p className="text-xs text-emerald-600 font-medium mb-1">Your Assigned Sectors:</p>
            <div className="flex flex-wrap gap-1">
              {userAssignedSectors.map(sector => (
                <Badge key={sector} variant="secondary" className="bg-emerald-100 text-emerald-700 text-xs">
                  {sector}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* For Mohalla Head - Show their mohalla info */}
        {isMohallaHead && !isAdmin && userMohalla && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs text-amber-700 font-medium">Your Mohalla: {userMohalla}</p>
            <p className="text-xs text-amber-600 mt-1">
              You can manage KG assignments for sectors in {userMohalla}
            </p>
          </div>
        )}

        {/* For users who are ALSO KG - Show their KG sectors */}
        {isAlsoKG && kgSectors?.length > 0 && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <p className="text-xs text-emerald-700 font-medium mb-1">Your Distribution Sectors (as KG):</p>
            <div className="flex flex-wrap gap-1">
              {kgSectors.map(sector => (
                <Badge key={sector} variant="secondary" className="bg-emerald-100 text-emerald-700 text-xs">
                  {sector}
                </Badge>
              ))}
            </div>
            <div className="mt-2 text-xs text-emerald-600">
              KG Progress: {kgProgress.processed}/{kgProgress.total} ({kgProgressPercent}%)
            </div>
          </div>
        )}

        {/* Stats - Different for each role */}
        {isKhidmatGuzar ? (
          <div className="grid grid-cols-2 gap-2 text-center">
            <div className="bg-slate-50 rounded-lg p-2">
              <p className="text-lg font-bold text-navy">{userAssignedSectors?.length || 0}</p>
              <p className="text-xs text-slate-500">Your Sectors</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-2">
              <p className="text-lg font-bold text-emerald-600">{progressPercent}%</p>
              <p className="text-xs text-slate-500">Completed</p>
            </div>
          </div>
        ) : isMohallaHead && !isAdmin ? (
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-slate-50 rounded-lg p-2">
              <p className="text-lg font-bold text-navy">{mohallaProgress.sectors || 0}</p>
              <p className="text-xs text-slate-500">MH Sectors</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-2">
              <p className="text-lg font-bold text-purple-600">{mohallaProgress.kg_assigned || 0}</p>
              <p className="text-xs text-slate-500">KG Assigned</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-2">
              <p className="text-lg font-bold text-emerald-600">{isAlsoKG ? kgSectors?.length || 0 : progressPercent + '%'}</p>
              <p className="text-xs text-slate-500">{isAlsoKG ? 'Your KG Sectors' : 'Progress'}</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-2 text-center">
            <div className="bg-slate-50 rounded-lg p-2">
              <p className="text-lg font-bold text-navy">{event.sectors?.length || 0}</p>
              <p className="text-xs text-slate-500">Sectors</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-2">
              <p className="text-lg font-bold text-navy">{event.incharge_count || 0}</p>
              <p className="text-xs text-slate-500">Incharges</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-2">
              <p className="text-lg font-bold text-navy">{event.khidmat_count || 0}</p>
              <p className="text-xs text-slate-500">Khidmat</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-2">
              <p className="text-lg font-bold text-orange-600">{event.returns_count || 0}</p>
              <p className="text-xs text-slate-500">Returns</p>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        {event.status === 'active' && displayProgress.total > 0 && (
          <div>
            <div className="flex justify-between text-xs text-slate-500 mb-1">
              <span>{isKhidmatGuzar ? 'Your Progress' : 'Distribution Progress'}</span>
              <span>{displayProgress.processed}/{displayProgress.total} ({progressPercent}%)</span>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-emerald-500 transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            {displayProgress.not_done > 0 && (
              <p className="text-xs text-orange-600 mt-1">
                {displayProgress.not_done} returns pending
              </p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button 
            variant={isKhidmatGuzar ? "default" : "outline"} 
            size="sm" 
            onClick={onView} 
            className={`flex-1 ${isKhidmatGuzar ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
          >
            <Eye className="w-4 h-4 mr-1" />
            {isKhidmatGuzar ? 'Start Distribution' : 'View'}
          </Button>
          {isAdmin && event.status === 'active' && (
            <>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onComplete}
                className="text-emerald-600 hover:text-emerald-700"
              >
                <CheckCircle className="w-4 h-4" />
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onDelete}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Create Event Dialog
function CreateEventDialog({ api, onClose, onSuccess }) {
  const [mohallas, setMohallas] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    event_date: new Date().toISOString().split('T')[0],
    event_type: 'distribution',
    sectors: [],
    notes: ''
  });
  const [selectedMohalla, setSelectedMohalla] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchMohallasAndSectors();
  }, []);

  const fetchMohallasAndSectors = async () => {
    try {
      const [mRes, sRes] = await Promise.all([
        api.get('/mohallas'),
        api.get('/sectors')
      ]);
      setMohallas(mRes.data.mohallas || []);
      setSectors(sRes.data.sectors || []);
    } catch (error) {
      console.error('Failed to fetch data');
    }
  };

  // Handle Mohalla selection - auto-select all sectors under it
  const handleMohallaSelect = (mohallaName) => {
    setSelectedMohalla(mohallaName);
    if (mohallaName) {
      const mohallaSectors = sectors.filter(s => s.mohalla === mohallaName).map(s => s.name);
      setFormData(prev => ({
        ...prev,
        sectors: [...new Set([...prev.sectors, ...mohallaSectors])]
      }));
    }
  };

  const toggleSector = (sectorName) => {
    setFormData(prev => ({
      ...prev,
      sectors: prev.sectors.includes(sectorName)
        ? prev.sectors.filter(s => s !== sectorName)
        : [...prev.sectors, sectorName]
    }));
  };

  const selectAllSectors = () => {
    setFormData(prev => ({
      ...prev,
      sectors: sectors.map(s => s.name)
    }));
  };

  const clearSectors = () => {
    setFormData(prev => ({ ...prev, sectors: [] }));
    setSelectedMohalla('');
  };

  // Group sectors by mohalla for display
  const sectorsByMohalla = sectors.reduce((acc, sector) => {
    const mohalla = sector.mohalla || 'Other';
    if (!acc[mohalla]) acc[mohalla] = [];
    acc[mohalla].push(sector);
    return acc;
  }, {});

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Event name is required');
      return;
    }
    if (formData.sectors.length === 0) {
      toast.error('Please select at least one sector');
      return;
    }

    setLoading(true);
    try {
      await api.post('/events', formData);
      toast.success('Event created successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-navy" />
            Create New Event
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="form-label">Event Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., FMB Distribution - 15 Jan"
                data-testid="event-name-input"
              />
            </div>
            <div>
              <Label className="form-label">Event Date *</Label>
              <Input
                type="date"
                value={formData.event_date}
                onChange={(e) => setFormData({...formData, event_date: e.target.value})}
                data-testid="event-date-input"
              />
            </div>
          </div>

          <div>
            <Label className="form-label">Description</Label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Brief description of the event"
            />
          </div>

          {/* Event Type Selection */}
          <div>
            <Label className="form-label">Event Type *</Label>
            <Select 
              value={formData.event_type} 
              onValueChange={(value) => setFormData({...formData, event_type: value})}
            >
              <SelectTrigger data-testid="event-type-select">
                <SelectValue placeholder="Select event type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="distribution">General Distribution</SelectItem>
                <SelectItem value="fmb_distribution">FMB Distribution (Thaali)</SelectItem>
                <SelectItem value="toloba">Toloba Event</SelectItem>
                <SelectItem value="special_occasion">Special Occasion</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1">
              {formData.event_type === 'fmb_distribution' 
                ? 'Only FMB Going families will be shown for distribution'
                : 'All families in selected sectors will be shown'
              }
            </p>
          </div>

          {/* Mohalla Selection - Auto-selects all sectors */}
          <div>
            <Label className="form-label">Quick Select: Mohalla (auto-selects all sectors)</Label>
            <Select value={selectedMohalla} onValueChange={handleMohallaSelect}>
              <SelectTrigger>
                <SelectValue placeholder="Select Mohalla to auto-select sectors..." />
              </SelectTrigger>
              <SelectContent>
                {mohallas.map(m => (
                  <SelectItem key={m.id || m.name} value={m.name}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sector Selection - Grouped by Mohalla */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="form-label">Select Sectors * ({formData.sectors.length} selected)</Label>
              <div className="flex gap-2">
                <Button variant="link" size="sm" onClick={selectAllSectors} className="text-xs">
                  Select All
                </Button>
                <Button variant="link" size="sm" onClick={clearSectors} className="text-xs text-red-500">
                  Clear All
                </Button>
              </div>
            </div>
            <div className="border rounded-lg p-3 max-h-64 overflow-y-auto bg-slate-50">
              {Object.entries(sectorsByMohalla).map(([mohalla, mohallaSectors]) => (
                <div key={mohalla} className="mb-4 last:mb-0">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-sm text-navy">{mohalla}</h4>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-xs h-6"
                      onClick={() => {
                        const sectorNames = mohallaSectors.map(s => s.name);
                        const allSelected = sectorNames.every(s => formData.sectors.includes(s));
                        if (allSelected) {
                          setFormData(prev => ({
                            ...prev,
                            sectors: prev.sectors.filter(s => !sectorNames.includes(s))
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            sectors: [...new Set([...prev.sectors, ...sectorNames])]
                          }));
                        }
                      }}
                    >
                      {mohallaSectors.every(s => formData.sectors.includes(s.name)) ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {mohallaSectors.map(sector => (
                      <label 
                        key={sector.id || sector.name}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors text-sm ${
                          formData.sectors.includes(sector.name) 
                            ? 'bg-navy/10 border border-navy' 
                            : 'bg-white border border-slate-200 hover:bg-slate-100'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.sectors.includes(sector.name)}
                          onChange={() => toggleSector(sector.name)}
                          className="w-3 h-3"
                        />
                        <span className="truncate">{sector.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="form-label">Notes</Label>
            <Input
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Any additional notes"
            />
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button onClick={handleSubmit} disabled={loading} className="bg-navy hover:bg-navy/90">
            {loading ? 'Creating...' : 'Create Event'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Event Details Dialog with full functionality
function EventDetailsDialog({ event, api, user, isAdmin, onClose, onRefresh, canExportExcel }) {
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAssignIncharge, setShowAssignIncharge] = useState(false);
  const [showAssignKhidmat, setShowAssignKhidmat] = useState(false);
  const [showAssignMohallaHead, setShowAssignMohallaHead] = useState(false);
  const [showReturnsReport, setShowReturnsReport] = useState(false);
  const [showCardDesigner, setShowCardDesigner] = useState(false);
  const [isEventIncharge, setIsEventIncharge] = useState(false);
  const [isMohallaHead, setIsMohallaHead] = useState(false);
  const [isKhidmatGuzar, setIsKhidmatGuzar] = useState(false);
  const [userMohalla, setUserMohalla] = useState(null);
  const [userAssignedSectors, setUserAssignedSectors] = useState([]);

  useEffect(() => {
    fetchEventDetails();
  }, [event.id]);

  // Check user access rights
  useEffect(() => {
    if (eventData && user) {
      const userItsId = user.its_id;
      const userMemberId = user.member_id;
      
      // Check if Event Incharge
      const incharges = eventData.event_incharges || [];
      setIsEventIncharge(incharges.some(i => i.its_id === userItsId));
      
      // Check if Mohalla Head
      const mohallaHeads = eventData.mohalla_heads || [];
      const headAssignment = mohallaHeads.find(h => h.its_id === userItsId);
      if (headAssignment) {
        setIsMohallaHead(true);
        setUserMohalla(headAssignment.mohalla);
      }
      
      // Check if Khidmat Guzar
      const khidmatAssignments = eventData.khidmat_assignments || [];
      const userKhidmatSectors = khidmatAssignments
        .filter(kg => kg.member_id === userMemberId || kg.its_id === userItsId)
        .map(kg => kg.sector);
      
      if (userKhidmatSectors.length > 0) {
        setIsKhidmatGuzar(true);
        // If initialSector is specified (clicked from sector card), show ONLY that sector
        if (event.initialSector) {
          setUserAssignedSectors([event.initialSector]);
        } else {
          setUserAssignedSectors(userKhidmatSectors);
        }
        // Auto-switch to distribution tab for Khidmat Guzar
        if (!isAdmin && !isEventIncharge && !isMohallaHead) {
          setActiveTab('distribution');
        }
      }
      
      // Also check from event.userAssignedSectors (passed from parent)
      if (event.userAssignedSectors?.length > 0) {
        setIsKhidmatGuzar(true);
        // If initialSector is specified, show ONLY that sector
        if (event.initialSector) {
          setUserAssignedSectors([event.initialSector]);
        } else {
          setUserAssignedSectors(event.userAssignedSectors);
        }
        if (!isAdmin && !isMohallaHead) {
          setActiveTab('distribution');
        }
      }
      
      // Auto-switch to khidmat tab for Mohalla Head (to see their KG assignments)
      if (headAssignment && !isAdmin && !isEventIncharge) {
        setActiveTab('khidmat');
      }
    }
  }, [eventData, user, event.userAssignedSectors, event.initialSector, isAdmin]);

  const canManageKhidmat = isAdmin || isEventIncharge || isMohallaHead;
  const canManageIncharges = isAdmin;
  const canManageMohallaHeads = isAdmin || isEventIncharge;

  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/events/${event.id}`);
      setEventData(response.data);
    } catch (error) {
      toast.error('Failed to fetch event details');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveIncharge = async (memberId) => {
    if (!window.confirm('Remove this Event Incharge?')) return;
    try {
      await api.delete(`/events/${event.id}/incharges/${memberId}`);
      toast.success('Event Incharge removed');
      fetchEventDetails();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove');
    }
  };

  const handleRemoveMohallaHead = async (memberId) => {
    if (!window.confirm('Remove this Mohalla Head?')) return;
    try {
      await api.delete(`/events/${event.id}/mohalla-heads/${memberId}`);
      toast.success('Mohalla Head removed');
      fetchEventDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove');
    }
  };

  const handleRemoveKhidmat = async (memberId, sector) => {
    if (!window.confirm('Remove this Khidmat Guzar?')) return;
    try {
      await api.delete(`/events/${event.id}/khidmat-guzars/${memberId}?sector=${encodeURIComponent(sector)}`);
      toast.success('Khidmat Guzar removed');
      fetchEventDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove');
    }
  };

  if (loading) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent>
          <div className="text-center py-8">
            <div className="w-6 h-6 border-2 border-navy border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Filter sectors for Mohalla Head or Khidmat Guzar
  const visibleSectors = (() => {
    // Khidmat Guzar - only their assigned sectors
    if (isKhidmatGuzar && !isAdmin && !isEventIncharge && !isMohallaHead) {
      return userAssignedSectors;
    }
    // Mohalla Head - only their mohalla sectors
    if (isMohallaHead && !isAdmin && !isEventIncharge) {
      return eventData?.sectors?.filter(s => {
        const sectorData = eventData?.sector_stats?.find(st => st.sector === s);
        return sectorData?.mohalla === userMohalla;
      });
    }
    return eventData?.sectors;
  })();

  // Check if user is pure Khidmat Guzar (not admin/incharge/mohalla head)
  const isPureKhidmatGuzar = isKhidmatGuzar && !isAdmin && !isEventIncharge && !isMohallaHead;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-navy" />
              {eventData?.name}
            </DialogTitle>
            <div className="flex items-center gap-2">
              {isPureKhidmatGuzar && (
                <Badge className="bg-emerald-500">Khidmat Guzar</Badge>
              )}
              {isEventIncharge && !isAdmin && (
                <Badge className="bg-purple-500">Event Incharge</Badge>
              )}
              {isMohallaHead && !isAdmin && !isEventIncharge && (
                <Badge className="bg-amber-500">Event Mohalla Head ({userMohalla})</Badge>
              )}
              <Badge className={eventData?.status === 'completed' ? 'bg-emerald-500' : 'bg-blue-500'}>
                {eventData?.status === 'completed' ? 'Completed' : 'Active'}
              </Badge>
            </div>
          </div>
          <p className="text-sm text-slate-500">
            {eventData?.event_date ? new Date(eventData.event_date).toLocaleDateString('en-IN', { 
              weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' 
            }) : ''}
          </p>
          {isPureKhidmatGuzar && (
            <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded">
              <p className="text-sm text-emerald-700">
                <strong>{userAssignedSectors.length === 1 ? 'Your Sector:' : 'Your Assigned Sectors:'}</strong> {userAssignedSectors.join(', ')}
              </p>
              <p className="text-xs text-emerald-600 mt-1">
                Mark distribution status for each family in {userAssignedSectors.length === 1 ? 'this sector' : 'your sector(s)'}.
              </p>
            </div>
          )}
          {(isEventIncharge || isMohallaHead) && !isAdmin && !isPureKhidmatGuzar && (
            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
              <strong>Your Access:</strong> {isEventIncharge 
                ? 'You can assign Khidmat Guzars to all sectors and update distribution status.'
                : `You can manage Khidmat Guzars for ${userMohalla} sectors only.`
              }
            </div>
          )}
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Simplified tabs for Khidmat Guzar */}
          {isPureKhidmatGuzar ? (
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="distribution">My Distribution</TabsTrigger>
              <TabsTrigger value="returns" className="flex items-center gap-1">
                <RotateCcw className="w-3 h-3" />
                Returns
              </TabsTrigger>
            </TabsList>
          ) : isMohallaHead && !isAdmin && !isEventIncharge ? (
            /* Simplified tabs for Mohalla Head - only their sectors and KG assignments */
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="khidmat">My KG Assignments</TabsTrigger>
              <TabsTrigger value="distribution">Distribution</TabsTrigger>
              <TabsTrigger value="returns" className="flex items-center gap-1">
                <RotateCcw className="w-3 h-3" />
                Returns
              </TabsTrigger>
            </TabsList>
          ) : (
            <div className="overflow-x-auto -mx-2 px-2">
              <TabsList className="inline-flex w-max min-w-full md:grid md:w-full md:grid-cols-6">
                <TabsTrigger value="overview" className="text-xs md:text-sm whitespace-nowrap">Overview</TabsTrigger>
                <TabsTrigger value="incharges" className="text-xs md:text-sm whitespace-nowrap">Incharges</TabsTrigger>
                <TabsTrigger value="khidmat" className="text-xs md:text-sm whitespace-nowrap">Khidmat</TabsTrigger>
                <TabsTrigger value="distribution" className="text-xs md:text-sm whitespace-nowrap">Distribution</TabsTrigger>
                <TabsTrigger value="registrations" className="text-xs md:text-sm whitespace-nowrap" data-testid="event-tab-registrations">Public Registrations</TabsTrigger>
                <TabsTrigger value="returns" className="flex items-center gap-1 text-xs md:text-sm whitespace-nowrap">
                  <RotateCcw className="w-3 h-3" />
                  Returns
                </TabsTrigger>
              </TabsList>
            </div>
          )}

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            {eventData?.description && (
              <p className="text-slate-600">{eventData.description}</p>
            )}

            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <h4 className="font-medium mb-2">Sectors Involved ({eventData?.sectors?.length})</h4>
                  <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                    {eventData?.sectors?.map(sector => (
                      <Badge key={sector} variant="outline" className="text-xs">{sector}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <h4 className="font-medium mb-2">Quick Stats</h4>
                  <div className="space-y-1 text-sm">
                    <p>Event Incharges: <strong>{eventData?.event_incharges?.length || 0}</strong></p>
                    <p>Event Mohalla Heads: <strong>{eventData?.mohalla_heads?.length || 0}</strong></p>
                    <p>Sectors Assigned: <strong>{eventData?.khidmat_assignments?.length || 0}</strong> / {eventData?.sectors?.length || 0}</p>
                    <p>Returns/Not Delivered: <strong className="text-orange-600">{eventData?.returns_count || 0}</strong></p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sector-wise Progress with Distribution Status - Sorted: Completed first, then Pending */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-navy" />
                  Sector-wise Distribution Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {/* Sort sectors: Completed (100%) first, then by percentage descending */}
                  {[...(eventData?.sector_stats || [])]
                    .map(stat => ({
                      ...stat,
                      percent: stat.total_families > 0 ? Math.round((stat.done / stat.total_families) * 100) : 0,
                      isComplete: stat.total_families > 0 && stat.done >= stat.total_families && (!stat.returns || stat.returns === 0)
                    }))
                    .sort((a, b) => {
                      // Completed first (100% with no returns)
                      if (a.isComplete && !b.isComplete) return -1;
                      if (!a.isComplete && b.isComplete) return 1;
                      // Then by percentage descending
                      return b.percent - a.percent;
                    })
                    .map(stat => {
                    const percent = stat.percent;
                    const isComplete = stat.isComplete;
                    const statusColor = isComplete ? 'bg-emerald-500' :
                                       stat.status === 'in_progress' ? 'bg-blue-500' :
                                       stat.status === 'ready' ? 'bg-amber-500' : 'bg-slate-400';
                    const statusText = isComplete ? '✓ 100% Complete' :
                                      stat.status === 'completed' ? 'Completed' :
                                      stat.status === 'in_progress' ? 'In Progress' :
                                      stat.status === 'ready' ? 'Ready' : 'Not Assigned';
                    return (
                      <div key={stat.sector} className={`p-3 border rounded-lg ${isComplete ? 'bg-emerald-50 border-emerald-200' : ''}`}>
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            {isComplete && <CheckCircle className="w-4 h-4 text-emerald-600" />}
                            <span className="font-medium">{stat.sector}</span>
                            <span className="text-xs text-slate-500">({stat.mohalla})</span>
                          </div>
                          <Badge className={`${statusColor} text-white text-xs`}>
                            {statusText}
                          </Badge>
                        </div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-600">
                            {stat.khidmat_guzars?.length || 0} KG assigned
                          </span>
                          <span className="text-slate-700 font-medium">
                            {stat.done}/{stat.total_families} ({percent}%)
                          </span>
                        </div>
                        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${isComplete ? 'bg-emerald-500' : 'bg-blue-500'}`}
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                        {stat.returns > 0 && (
                          <p className="text-xs text-orange-600 mt-1">
                            ⚠️ {stat.returns} returns pending
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Incharges Tab (Event Incharges + Mohalla Heads) */}
          <TabsContent value="incharges" className="space-y-6">
            {/* Event Incharges Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Event Incharges
                </h3>
                {isAdmin && eventData?.status === 'active' && (
                  <Button onClick={() => setShowAssignIncharge(true)} size="sm" className="bg-navy">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Assign
                  </Button>
                )}
              </div>
              {eventData?.event_incharges?.length === 0 ? (
                <p className="text-slate-500 text-sm">No Event Incharges assigned</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>ITS</TableHead>
                      <TableHead>Mobile</TableHead>
                      <TableHead>Assigned</TableHead>
                      {isAdmin && eventData?.status === 'active' && <TableHead className="text-right">Action</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {eventData?.event_incharges?.map((incharge, idx) => (
                      <TableRow key={incharge.member_id || idx}>
                        <TableCell className="font-medium">{incharge.full_name || incharge.member_name}</TableCell>
                        <TableCell className="font-mono text-sm">{incharge.its_id || '-'}</TableCell>
                        <TableCell>{incharge.mobile || '-'}</TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {new Date(incharge.assigned_at).toLocaleDateString()}
                        </TableCell>
                        {isAdmin && eventData?.status === 'active' && (
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleRemoveIncharge(incharge.member_id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            {/* Event Mohalla Heads Section (Temporary, Event-Only) */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Event Mohalla Heads
                  </h3>
                  <p className="text-xs text-amber-600 mt-1">
                    ⚠️ Temporary assignment for this event only. Access expires when event completes.
                  </p>
                </div>
                {(isAdmin || isEventIncharge) && eventData?.status === 'active' && (
                  <Button onClick={() => setShowAssignMohallaHead(true)} size="sm" className="bg-amber-600 hover:bg-amber-700">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Assign Event Mohalla Head
                  </Button>
                )}
              </div>
              {!eventData?.mohalla_heads?.length ? (
                <p className="text-slate-500 text-sm">No Event Mohalla Heads assigned</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Assigned Mohalla</TableHead>
                      <TableHead>ITS</TableHead>
                      <TableHead>Mobile</TableHead>
                      <TableHead>Assigned On</TableHead>
                      {(isAdmin || isEventIncharge) && eventData?.status === 'active' && <TableHead className="text-right">Action</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {eventData?.mohalla_heads?.map((head, idx) => (
                      <TableRow key={head.member_id || idx}>
                        <TableCell className="font-medium">{head.full_name || head.member_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-amber-50">
                            {head.mohalla}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{head.its_id || '-'}</TableCell>
                        <TableCell>{head.mobile || '-'}</TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {head.assigned_at ? new Date(head.assigned_at).toLocaleDateString() : '-'}
                        </TableCell>
                        {(isAdmin || isEventIncharge) && eventData?.status === 'active' && (
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleRemoveMohallaHead(head.member_id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* Khidmat Guzars Tab */}
          <TabsContent value="khidmat" className="space-y-4">
            {/* Mohalla Head info message */}
            {isMohallaHead && !isAdmin && !isEventIncharge && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Your Mohalla: {userMohalla}</strong>
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  You can assign and manage Khidmat Guzars only for sectors in {userMohalla}.
                </p>
              </div>
            )}
            
            {canManageKhidmat && eventData?.status === 'active' && (
              <div className="flex justify-end">
                <Button onClick={() => setShowAssignKhidmat(true)} className="bg-navy">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Assign Khidmat Guzar
                </Button>
              </div>
            )}

            {eventData?.khidmat_assignments?.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                <p>No Khidmat Guzars assigned yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {visibleSectors?.map(sector => {
                  const sectorKhidmats = eventData.khidmat_assignments.filter(kg => kg.sector === sector);
                  const canEdit = canManageKhidmat && eventData?.status === 'active';
                  
                  return (
                    <Card key={sector}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center justify-between">
                          <span>{sector}</span>
                          <Badge variant="outline">{sectorKhidmats.length}/3 assigned</Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {sectorKhidmats.length === 0 ? (
                          <p className="text-sm text-slate-500">No Khidmat Guzar assigned</p>
                        ) : (
                          <div className="space-y-2">
                            {sectorKhidmats.map((kg, idx) => (
                              <div key={kg.member_id || idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                                <div>
                                  <p className="font-medium text-sm">{kg.full_name || kg.member_name}</p>
                                  <p className="text-xs text-slate-500">ITS: {kg.its_id || 'N/A'} | {kg.mobile || 'N/A'}</p>
                                </div>
                                {canEdit && (
                                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleRemoveKhidmat(kg.member_id, sector)}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Distribution Tab */}
          <TabsContent value="distribution" className="space-y-4">
            <DistributionTab 
              event={eventData} 
              api={api} 
              onRefresh={fetchEventDetails}
              canEdit={(canManageKhidmat || isPureKhidmatGuzar) && eventData?.status === 'active'}
              userMohalla={isMohallaHead ? userMohalla : null}
              userAssignedSectors={isPureKhidmatGuzar ? userAssignedSectors : null}
            />
          </TabsContent>

          {/* Public Registrations Tab */}
          <TabsContent value="registrations" className="space-y-4">
            <div className="flex items-center justify-end">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowCardDesigner(true)}
                disabled={!isAdmin}
                data-testid="event-open-card-designer-btn"
              >
                <Settings className="w-4 h-4 mr-1" /> Configure Public Form & ID Card
              </Button>
            </div>
            <EventRegistrationsList api={api} eventId={event.id} isAdmin={isAdmin} />
          </TabsContent>

          {/* Returns Tab */}
          <TabsContent value="returns" className="space-y-4">
            <ReturnsTab 
              event={eventData}
              api={api}
              isAdmin={isAdmin}
              canExportExcel={canExportExcel}
              onRefresh={fetchEventDetails}
            />
          </TabsContent>
        </Tabs>

        {showCardDesigner && (
          <EventCardDesigner
            open={showCardDesigner}
            onClose={() => setShowCardDesigner(false)}
            api={api}
            event={eventData || event}
            onSaved={() => fetchEventDetails && fetchEventDetails()}
          />
        )}

        {/* Assign Dialogs */}
        {showAssignIncharge && (
          <AssignMemberDialog 
            title="Assign Event Incharge"
            api={api}
            eventId={event.id}
            assignType="incharge"
            onClose={() => setShowAssignIncharge(false)}
            onSuccess={() => { fetchEventDetails(); setShowAssignIncharge(false); onRefresh(); }}
          />
        )}

        {showAssignMohallaHead && (
          <AssignMohallaHeadDialog 
            api={api}
            eventId={event.id}
            eventSectors={eventData?.sectors || []}
            onClose={() => setShowAssignMohallaHead(false)}
            onSuccess={() => { fetchEventDetails(); setShowAssignMohallaHead(false); }}
          />
        )}

        {showAssignKhidmat && (
          <AssignKhidmatDialog 
            api={api}
            eventId={event.id}
            sectors={isMohallaHead && !isAdmin && !isEventIncharge 
              ? visibleSectors 
              : eventData?.sectors || []
            }
            existingAssignments={eventData?.khidmat_assignments || []}
            onClose={() => setShowAssignKhidmat(false)}
            onSuccess={() => { fetchEventDetails(); setShowAssignKhidmat(false); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// Assign Member Dialog for Event Incharge
function AssignMemberDialog({ title, api, eventId, assignType, onClose, onSuccess }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [searching, setSearching] = useState(false);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (!searchTerm.trim() || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }
    const delayDebounceFn = setTimeout(async () => {
      await performSearch(searchTerm);
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const performSearch = async (term) => {
    setSearching(true);
    try {
      const response = await api.get(`/members/search-fuzzy?q=${encodeURIComponent(term)}&limit=20`);
      setSearchResults(response.data.members || []);
    } catch (error) {
      try {
        const response = await api.get(`/members?search=${encodeURIComponent(term)}&limit=20`);
        setSearchResults(response.data.members || []);
      } catch (err) {
        console.error('Search failed');
      }
    } finally {
      setSearching(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedMember) {
      toast.error('Please select a member');
      return;
    }
    setAssigning(true);
    try {
      await api.post(`/events/${eventId}/incharges`, { member_id: selectedMember.id });
      toast.success('Assignment successful');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Assignment failed');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="form-label">Search by ITS / Name / Mobile</Label>
            <div className="relative">
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Start typing (e.g., 'murtaza raipur')..."
                className="pr-10"
              />
              {searching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-navy border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
          </div>

          {searchTerm.length >= 2 && (
            <div className="border rounded-lg max-h-52 overflow-y-auto">
              {searching ? (
                <div className="p-4 text-center text-slate-500">Searching...</div>
              ) : searchResults.length === 0 ? (
                <div className="p-4 text-center text-slate-500">
                  <AlertCircle className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                  No matching records found
                </div>
              ) : (
                searchResults.map(member => (
                  <div 
                    key={member.id}
                    onClick={() => setSelectedMember(member)}
                    className={`p-3 cursor-pointer border-b last:border-b-0 hover:bg-slate-50 ${
                      selectedMember?.id === member.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                    }`}
                  >
                    <p className="font-medium text-sm">{member.full_name}</p>
                    <div className="flex flex-wrap gap-2 mt-1 text-xs text-slate-500">
                      <span>ITS: {member.its_id || 'N/A'}</span>
                      <span>|</span>
                      <span>Mobile: {member.mobile || 'N/A'}</span>
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      {member.mohalla || 'N/A'} - {member.sector || 'N/A'}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {selectedMember && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm font-medium text-emerald-800">Selected:</p>
              <p className="text-sm text-emerald-700">{selectedMember.full_name}</p>
              <p className="text-xs text-emerald-600">
                ITS: {selectedMember.its_id || 'N/A'} | {selectedMember.mohalla} - {selectedMember.sector}
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button onClick={handleAssign} disabled={assigning || !selectedMember} className="bg-navy">
            {assigning ? 'Assigning...' : 'Assign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Assign Event Mohalla Head Dialog (Temporary, Event-Only - NO Masool/Masoola)
function AssignMohallaHeadDialog({ api, eventId, eventSectors, onClose, onSuccess }) {
  const [mohallas, setMohallas] = useState([]);
  const [selectedMohalla, setSelectedMohalla] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [searching, setSearching] = useState(false);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    fetchMohallas();
  }, []);

  const fetchMohallas = async () => {
    try {
      const response = await api.get('/mohallas');
      const allMohallas = response.data.mohallas || [];
      setMohallas(allMohallas);
    } catch (error) {
      console.error('Failed to fetch mohallas');
    }
  };

  useEffect(() => {
    if (!searchTerm.trim() || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }
    const delayDebounceFn = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await api.get(`/members/search-fuzzy?q=${encodeURIComponent(searchTerm)}&limit=20`);
        setSearchResults(response.data.members || []);
      } catch (error) {
        console.error('Search failed');
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const handleAssign = async () => {
    if (!selectedMember || !selectedMohalla) {
      toast.error('Please select a member and mohalla');
      return;
    }
    setAssigning(true);
    try {
      await api.post(`/events/${eventId}/mohalla-heads`, { 
        member_id: selectedMember.id,
        mohalla: selectedMohalla
      });
      toast.success('Event Mohalla Head assigned successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Assignment failed');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-amber-600" />
            Assign Event Mohalla Head
          </DialogTitle>
          <p className="text-xs text-amber-600 mt-1">
            ⚠️ This is a TEMPORARY assignment for this event only. Access will expire when the event completes.
          </p>
        </DialogHeader>
        <div className="space-y-4">
          {/* Mohalla Selection */}
          <div>
            <Label className="form-label">Select Mohalla to Manage *</Label>
            <Select value={selectedMohalla} onValueChange={setSelectedMohalla}>
              <SelectTrigger>
                <SelectValue placeholder="Choose mohalla..." />
              </SelectTrigger>
              <SelectContent>
                {mohallas.map(m => (
                  <SelectItem key={m.id || m.name} value={m.name}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1">
              The selected member will be able to assign Khidmat Guzars to sectors under this Mohalla.
            </p>
          </div>

          {/* Member Search */}
          <div>
            <Label className="form-label">Search Member (Real-time)</Label>
            <div className="relative">
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by ITS, name, or mobile..."
                className="pr-10"
              />
              {searching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
          </div>

          {searchTerm.length >= 2 && (
            <div className="border rounded-lg max-h-40 overflow-y-auto">
              {searchResults.length === 0 && !searching ? (
                <div className="p-4 text-center text-slate-500">No matching records found</div>
              ) : (
                searchResults.map(member => (
                  <div 
                    key={member.id}
                    onClick={() => setSelectedMember(member)}
                    className={`p-3 cursor-pointer border-b last:border-b-0 hover:bg-slate-50 ${
                      selectedMember?.id === member.id ? 'bg-amber-50 border-l-4 border-l-amber-500' : ''
                    }`}
                  >
                    <p className="font-medium text-sm">{member.full_name}</p>
                    <p className="text-xs text-slate-500">
                      ITS: {member.its_id || 'N/A'} | Mobile: {member.mobile || 'N/A'}
                    </p>
                    <p className="text-xs text-slate-400">{member.mohalla} - {member.sector}</p>
                  </div>
                ))
              )}
            </div>
          )}

          {selectedMember && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm font-medium text-amber-800">Selected Event Mohalla Head:</p>
              <p className="text-sm text-amber-700">{selectedMember.full_name}</p>
              <p className="text-xs text-amber-600">
                ITS: {selectedMember.its_id || 'N/A'} | Will manage: {selectedMohalla || 'Select mohalla'}
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button 
            onClick={handleAssign} 
            disabled={assigning || !selectedMember || !selectedMohalla} 
            className="bg-amber-600 hover:bg-amber-700"
          >
            {assigning ? 'Assigning...' : 'Assign Event Mohalla Head'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Assign Khidmat Guzar Dialog with Mohalla/Sector selection - Multi-select flow
function AssignKhidmatDialog({ api, eventId, sectors, existingAssignments, onClose, onSuccess }) {
  const [mohallas, setMohallas] = useState([]);
  const [allSectors, setAllSectors] = useState([]);
  const [selectedMohalla, setSelectedMohalla] = useState('');
  const [selectedSectors, setSelectedSectors] = useState([]);  // Changed to array for multi-select
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [localAssignments, setLocalAssignments] = useState(existingAssignments || []);

  useEffect(() => {
    fetchData();
  }, []);

  // Sync local assignments when prop changes
  useEffect(() => {
    setLocalAssignments(existingAssignments || []);
  }, [existingAssignments]);

  const fetchData = async () => {
    try {
      const [mRes, sRes] = await Promise.all([
        api.get('/mohallas'),
        api.get('/sectors')
      ]);
      setMohallas(mRes.data.mohallas || []);
      setAllSectors(sRes.data.sectors || []);
    } catch (error) {
      console.error('Failed to fetch data');
    }
  };

  // Handle Mohalla selection - show sectors under it
  const handleMohallaSelect = (mohallaName) => {
    setSelectedMohalla(mohallaName);
    setSelectedSectors([]); // Reset sector selection
    setSearchTerm('');
    setSearchResults([]);
  };

  // Handle Sector toggle (multi-select)
  const handleSectorToggle = (sectorName) => {
    setSelectedSectors(prev => {
      if (prev.includes(sectorName)) {
        return prev.filter(s => s !== sectorName);
      } else {
        return [...prev, sectorName];
      }
    });
    setSearchTerm('');
    setSearchResults([]);
  };

  // Real-time search when at least one sector is selected
  useEffect(() => {
    if (selectedSectors.length === 0 || !searchTerm.trim() || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }
    const delayDebounceFn = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await api.get(`/members/search-fuzzy?q=${encodeURIComponent(searchTerm)}&limit=20`);
        setSearchResults(response.data.members || []);
      } catch (error) {
        console.error('Search failed');
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, selectedSectors]);

  // Group available sectors by mohalla
  const sectorsByMohalla = sectors.reduce((acc, sectorName) => {
    const sector = allSectors.find(s => s.name === sectorName);
    const mohalla = sector?.mohalla || 'Other';
    if (!acc[mohalla]) acc[mohalla] = [];
    acc[mohalla].push(sectorName);
    return acc;
  }, {});

  // Get sectors for selected mohalla
  const availableSectors = selectedMohalla ? (sectorsByMohalla[selectedMohalla] || []) : [];

  // Get combined assignments for all selected sectors
  const selectedSectorAssignments = localAssignments.filter(a => selectedSectors.includes(a.sector));

  // Check if member is already assigned to any selected sector
  const isAlreadyAssigned = (memberId) => {
    return selectedSectorAssignments.some(a => a.member_id === memberId);
  };

  // Handle adding a Khidmat Guzar to ALL selected sectors
  const handleAddKhidmat = async (member) => {
    if (selectedSectors.length === 0) {
      toast.error('Please select at least one sector');
      return;
    }

    setAssigning(true);
    let successCount = 0;
    let errorMessages = [];

    for (const sector of selectedSectors) {
      const sectorCount = localAssignments.filter(a => a.sector === sector).length;
      const alreadyInSector = localAssignments.some(a => a.member_id === member.id && a.sector === sector);
      
      if (sectorCount >= 3) {
        errorMessages.push(`${sector}: Max 3 already assigned`);
        continue;
      }
      if (alreadyInSector) {
        errorMessages.push(`${sector}: Already assigned`);
        continue;
      }

      try {
        await api.post(`/events/${eventId}/khidmat-guzars`, { 
          member_id: member.id,
          sector: sector
        });
        // Update local state immediately
        setLocalAssignments(prev => [...prev, {
          member_id: member.id,
          member_name: member.full_name,
          its_id: member.its_id,
          mobile: member.mobile,
          sector: sector
        }]);
        successCount++;
      } catch (error) {
        errorMessages.push(`${sector}: ${error.response?.data?.detail || 'Failed'}`);
      }
    }

    setAssigning(false);
    
    if (successCount > 0) {
      toast.success(`${member.full_name} assigned to ${successCount} sector(s)`);
      setSearchTerm('');
      setSearchResults([]);
    }
    if (errorMessages.length > 0) {
      toast.error(errorMessages.join(', '));
    }
  };

  // Handle removing a Khidmat Guzar from a specific sector
  const handleRemoveKhidmat = async (memberId, sector) => {
    if (!window.confirm('Remove this Khidmat Guzar?')) return;
    try {
      await api.delete(`/events/${eventId}/khidmat-guzars/${memberId}?sector=${encodeURIComponent(sector)}`);
      setLocalAssignments(prev => prev.filter(a => !(a.member_id === memberId && a.sector === sector)));
      toast.success('Khidmat Guzar removed');
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Assign Khidmat Guzar
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Step 1: Mohalla Selection */}
          <div>
            <Label className="form-label flex items-center gap-2">
              <span className="bg-navy text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">1</span>
              Select Mohalla
            </Label>
            <Select value={selectedMohalla} onValueChange={handleMohallaSelect}>
              <SelectTrigger>
                <SelectValue placeholder="Select Mohalla..." />
              </SelectTrigger>
              <SelectContent>
                {Object.keys(sectorsByMohalla).map(mohalla => (
                  <SelectItem key={mohalla} value={mohalla}>{mohalla}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Step 2: Sector Selection (Multi-select) - shown after Mohalla selected */}
          {selectedMohalla && (
            <div>
              <Label className="form-label flex items-center gap-2">
                <span className="bg-navy text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">2</span>
                Select Sector(s) <span className="text-xs text-slate-500 font-normal">(click to select multiple)</span>
              </Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {availableSectors.map(sector => {
                  const count = localAssignments.filter(a => a.sector === sector).length;
                  const isSelected = selectedSectors.includes(sector);
                  return (
                    <button
                      key={sector}
                      onClick={() => handleSectorToggle(sector)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        isSelected 
                          ? 'bg-navy text-white shadow-md ring-2 ring-navy ring-offset-2' 
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                      } ${count >= 3 ? 'ring-2 ring-green-500' : ''}`}
                    >
                      {sector}
                      <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                        count >= 3 ? 'bg-green-500 text-white' : isSelected ? 'bg-white/20' : 'bg-slate-300'
                      }`}>
                        {count}/3
                      </span>
                    </button>
                  );
                })}
              </div>
              {selectedSectors.length > 0 && (
                <p className="text-xs text-emerald-600 mt-2">
                  ✓ {selectedSectors.length} sector(s) selected: {selectedSectors.join(', ')}
                </p>
              )}
            </div>
          )}

          {/* Step 3: Khidmat Guzar Assignment - shown after Sector(s) selected */}
          {selectedSectors.length > 0 && (
            <div className="border-t pt-4">
              <Label className="form-label flex items-center gap-2 mb-3">
                <span className="bg-navy text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">3</span>
                Assign Khidmat Guzars to <span className="font-bold text-navy">{selectedSectors.length} sector(s)</span>
              </Label>

              {/* Currently Assigned - grouped by sector */}
              <div className="mb-4 space-y-2">
                {selectedSectors.map(sector => {
                  const sectorAssigns = localAssignments.filter(a => a.sector === sector);
                  return (
                    <div key={sector} className="bg-slate-50 rounded-lg p-2">
                      <p className="text-xs font-medium text-slate-600 mb-1">{sector} ({sectorAssigns.length}/3)</p>
                      {sectorAssigns.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {sectorAssigns.map(kg => (
                            <div key={`${kg.member_id}-${sector}`} className="flex items-center gap-1 bg-white px-2 py-1 rounded-full border text-xs">
                              <span>{kg.member_name}</span>
                              <button 
                                onClick={() => handleRemoveKhidmat(kg.member_id, sector)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400">No Khidmat Guzar assigned yet</p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Add More - Search */}
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Add Khidmat Guzar (will be added to all selected sectors):</Label>
                <div className="relative">
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by ITS, name, or mobile..."
                    className="pr-10"
                    autoFocus
                  />
                  {searching && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="w-4 h-4 border-2 border-navy border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>

                {searchTerm.length >= 2 && (
                  <div className="border rounded-lg max-h-48 overflow-y-auto mt-2 shadow-sm">
                    {searchResults.length === 0 && !searching ? (
                      <div className="p-4 text-center text-slate-500">No matching records found</div>
                    ) : (
                      searchResults.map(member => {
                        const alreadyAssigned = isAlreadyAssigned(member.id);
                        return (
                          <div 
                            key={member.id}
                            className={`p-3 border-b last:border-b-0 flex items-center justify-between ${
                              alreadyAssigned ? 'bg-slate-100 opacity-60' : 'hover:bg-blue-50 cursor-pointer'
                            }`}
                          >
                            <div>
                              <p className="font-medium text-sm">{member.full_name}</p>
                              <p className="text-xs text-slate-500">
                                ITS: {member.its_id || 'N/A'} | {member.mohalla} - {member.sector}
                              </p>
                            </div>
                            {alreadyAssigned ? (
                              <Badge variant="secondary" className="text-xs">Assigned</Badge>
                            ) : (
                              <Button 
                                size="sm" 
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handleAddKhidmat(member)}
                                disabled={assigning}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Add to {selectedSectors.length} sector(s)
                              </Button>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { onSuccess(); onClose(); }}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Distribution Tab Component with Return/Not Delivered options
function DistributionTab({ event, api, onRefresh, canEdit, userMohalla, userAssignedSectors }) {
  const [selectedSector, setSelectedSector] = useState('');
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  // Filter sectors - Khidmat Guzar sees only assigned sectors, Mohalla Head sees mohalla sectors
  const [includeAll, setIncludeAll] = useState(false);
  
  const availableSectors = (() => {
    if (userAssignedSectors?.length > 0) {
      return userAssignedSectors;
    }
    if (userMohalla) {
      return event?.sectors?.filter(s => {
        const stat = event?.sector_stats?.find(st => st.sector === s);
        return stat?.mohalla === userMohalla;
      });
    }
    return event?.sectors;
  })();

  useEffect(() => {
    if (availableSectors?.length > 0 && !selectedSector) {
      setSelectedSector(availableSectors[0]);
    }
  }, [availableSectors]);

  useEffect(() => {
    if (selectedSector) {
      fetchFamilies();
    }
  }, [selectedSector, statusFilter, includeAll]);

  const fetchFamilies = async () => {
    setLoading(true);
    try {
      let url = `/events/${event.id}/distributions?sector=${encodeURIComponent(selectedSector)}`;
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      if (includeAll) {
        url += `&include_all=true`;
      }
      const response = await api.get(url);
      setFamilies(response.data.families || []);
    } catch (error) {
      toast.error('Failed to fetch families');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (familyId, status, reason = null, givenToOther = null, sendWhatsapp = true) => {
    try {
      const response = await api.post(`/events/${event.id}/distributions`, {
        family_id: familyId,
        sector: selectedSector,
        status: status,
        reason: reason,
        given_to_other: givenToOther,
        send_whatsapp: sendWhatsapp
      });
      
      // Show appropriate message
      if (response.data.whatsapp_sent) {
        toast.success(`Status updated & WhatsApp sent`);
      } else {
        toast.success('Status updated');
      }
      
      fetchFamilies();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleMarkDone = async (family) => {
    await updateStatus(family.family_id, 'done');
  };

  const handleNotDelivered = async (family) => {
    const reasons = ['Home Locked', 'Not in Town', 'Refused', 'Other'];
    const reason = prompt(`Select reason:\n1. Home Locked\n2. Not in Town\n3. Refused\n4. Other\n\nEnter reason or number:`);
    if (reason) {
      const mappedReason = reasons[parseInt(reason) - 1] || reason;
      await updateStatus(family.family_id, 'not_done', mappedReason);
    }
  };

  const handleUndo = async (family) => {
    if (window.confirm('Reset this family to Pending status?')) {
      await updateStatus(family.family_id, 'pending', null, null, false);
    }
  };

  // Get sector stats for badge display
  const getSectorStats = (sector) => {
    const stat = event?.sector_stats?.find(st => st.sector === sector);
    if (!stat) return { done: 0, total: 0 };
    return { done: stat.done || 0, total: stat.total_families || 0, not_done: stat.not_done || 0 };
  };

  return (
    <div className="space-y-4">
      {/* Sector Tabs */}
      {availableSectors?.length > 1 ? (
        <div className="border-b">
          <div className="flex flex-wrap gap-1">
            {availableSectors?.map(sector => {
              const stats = getSectorStats(sector);
              const isActive = selectedSector === sector;
              const isComplete = stats.done + stats.not_done >= stats.total && stats.total > 0;
              return (
                <button
                  key={sector}
                  onClick={() => setSelectedSector(sector)}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-all border-b-2 ${
                    isActive 
                      ? 'bg-navy text-white border-navy' 
                      : 'bg-slate-50 text-slate-600 border-transparent hover:bg-slate-100'
                  } ${isComplete && !isActive ? 'ring-2 ring-emerald-500' : ''}`}
                >
                  {sector}
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                    isComplete ? 'bg-emerald-500 text-white' : isActive ? 'bg-white/20' : 'bg-slate-200'
                  }`}>
                    {stats.done}/{stats.total}
                  </span>
                  {stats.not_done > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-orange-500 text-white">
                      {stats.not_done}↩
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ) : availableSectors?.length === 1 ? (
        <div className="bg-gradient-to-r from-navy to-blue-600 rounded-lg p-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 rounded-lg p-2">
                <Home className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold">{availableSectors[0]}</h3>
                <p className="text-white/70 text-sm">Distribution List</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold">{getSectorStats(availableSectors[0]).total}</p>
                <p className="text-xs text-white/70">Total</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-300">{getSectorStats(availableSectors[0]).done}</p>
                <p className="text-xs text-white/70">Done</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-300">{getSectorStats(availableSectors[0]).not_done}</p>
                <p className="text-xs text-white/70">Returns</p>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Status Filter */}
      <div className="flex justify-between items-center">
        {families.length > 0 && families[0]?.is_fmb_event && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeAll}
              onChange={(e) => setIncludeAll(e.target.checked)}
              className="w-4 h-4 rounded"
            />
            <span className="text-slate-600">Show all families (including non-FMB)</span>
          </label>
        )}
        {(!families.length || !families[0]?.is_fmb_event) && <div />}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="done">Done</SelectItem>
            <SelectItem value="not_done">Not Delivered</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="w-6 h-6 border-2 border-navy border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : families.length === 0 ? (
        <div className="text-center py-8 text-slate-500">No families found</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Thaali #</TableHead>
              <TableHead>HOF Name</TableHead>
              <TableHead>Mobile</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Reason</TableHead>
              {canEdit && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {families.map(family => (
              <TableRow key={family.family_id}>
                <TableCell className="font-mono">{family.thaali_number || '-'}</TableCell>
                <TableCell className="font-medium">{family.hof_name}</TableCell>
                <TableCell>{family.mobile || '-'}</TableCell>
                <TableCell>
                  {family.distribution_status === 'done' && (
                    <Badge className="bg-emerald-500">Done ✓</Badge>
                  )}
                  {family.distribution_status === 'not_done' && (
                    <Badge className="bg-orange-500">Not Delivered</Badge>
                  )}
                  {family.distribution_status === 'pending' && (
                    <Badge variant="outline">Pending</Badge>
                  )}
                  {family.distribution_status === 'not_applicable' && (
                    <Badge className="bg-slate-400">N/A</Badge>
                  )}
                </TableCell>
                <TableCell className="text-sm text-slate-500">
                  {family.distribution_status === 'not_applicable' 
                    ? <span className="text-slate-400 italic">Not FMB Going</span>
                    : (family.reason || '-')
                  }
                </TableCell>
                {canEdit && (
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      {family.distribution_status === 'pending' && (
                        <>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-emerald-600 hover:bg-emerald-50"
                            onClick={() => handleMarkDone(family)}
                            title="Mark Done (WhatsApp will be sent)"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-orange-600 hover:bg-orange-50"
                            onClick={() => handleNotDelivered(family)}
                            title="Not Delivered (WhatsApp will be sent with reason)"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {family.distribution_status === 'not_applicable' && (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                      {(family.distribution_status === 'done' || family.distribution_status === 'not_done') && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-slate-600 hover:bg-slate-100"
                          onClick={() => handleUndo(family)}
                          title="Undo - Reset to Pending"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// Returns Tab Component - Simplified with Mark Complete
function ReturnsTab({ event, api, isAdmin, canExportExcel, onRefresh }) {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMohalla, setSelectedMohalla] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [completingReturn, setCompletingReturn] = useState(null);

  useEffect(() => {
    fetchReturns();
  }, [selectedMohalla]);

  const fetchReturns = async () => {
    setLoading(true);
    try {
      let url = `/events/${event.id}/returns`;
      if (selectedMohalla !== 'all') {
        url += `?mohalla=${encodeURIComponent(selectedMohalla)}`;
      }
      const response = await api.get(url);
      setReturns(response.data.returns || []);
    } catch (error) {
      console.error('Failed to fetch returns:', error);
      const notDelivered = event?.distributions?.filter(d => d.status === 'not_done') || [];
      setReturns(notDelivered);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkComplete = async (familyId) => {
    try {
      setCompletingReturn(familyId);
      await api.post(`/events/${event.id}/returns/receive`, {
        family_id: familyId,
        remarks: 'Return collected'
      });
      toast.success('Return marked as complete');
      fetchReturns();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to mark complete');
    } finally {
      setCompletingReturn(null);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      let url = `/events/${event.id}/returns/export?format=${format}`;
      if (selectedMohalla !== 'all') {
        url += `&mohalla=${encodeURIComponent(selectedMohalla)}`;
      }
      
      const response = await api.get(url, { responseType: 'blob' });
      
      const blob = new Blob([response.data], { 
        type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const urlObj = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = urlObj;
      link.download = `returns_${event.name.replace(/\s+/g, '_')}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(urlObj);
      
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Get unique mohallas from returns
  const mohallas = [...new Set(returns.map(r => r.mohalla).filter(Boolean))];
  
  // Separate pending and completed returns
  const pendingReturns = returns.filter(r => !r.return_received);
  const completedReturns = returns.filter(r => r.return_received);

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-2 md:gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-2 md:p-4 text-center">
            <p className="text-lg md:text-2xl font-bold text-orange-600">{returns.length}</p>
            <p className="text-[10px] md:text-xs text-slate-500">Total Returns</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-2 md:p-4 text-center">
            <p className="text-lg md:text-2xl font-bold text-red-600">{pendingReturns.length}</p>
            <p className="text-[10px] md:text-xs text-slate-500">Pending</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-2 md:p-4 text-center">
            <p className="text-lg md:text-2xl font-bold text-emerald-600">{completedReturns.length}</p>
            <p className="text-[10px] md:text-xs text-slate-500">Collected</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="flex gap-2 md:gap-4 items-end">
          <div>
            <Label className="form-label">Filter by Mohalla</Label>
            <Select value={selectedMohalla} onValueChange={setSelectedMohalla}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Mohallas</SelectItem>
                {mohallas.map(m => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {isAdmin && returns.length > 0 && (
          <div className="flex gap-2">
            {canExportExcel && (
              <Button variant="outline" size="sm" onClick={() => handleExport('excel')} disabled={exporting}>
                <Download className="w-4 h-4 mr-2" />
                Excel
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} disabled={exporting}>
              <FileText className="w-4 h-4 mr-2" />
              PDF
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="w-6 h-6 border-2 border-navy border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : returns.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <RotateCcw className="w-12 h-12 mx-auto text-slate-300 mb-2" />
          <p>No returned/not delivered packets</p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-4 md:mx-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap">Sr</TableHead>
                <TableHead className="whitespace-nowrap">ITS</TableHead>
                <TableHead className="whitespace-nowrap min-w-[150px]">HOF Name</TableHead>
                <TableHead className="whitespace-nowrap">Mobile</TableHead>
                <TableHead className="hidden md:table-cell whitespace-nowrap">Mohalla</TableHead>
                <TableHead className="whitespace-nowrap">Sector</TableHead>
                <TableHead className="hidden sm:table-cell whitespace-nowrap">Reason</TableHead>
                <TableHead className="whitespace-nowrap">Status</TableHead>
                {isAdmin && <TableHead className="whitespace-nowrap">Action</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {returns.map((item, idx) => (
                <TableRow key={item.family_id || idx} className={item.return_received ? 'bg-green-50' : ''}>
                  <TableCell className="text-xs md:text-sm">{idx + 1}</TableCell>
                  <TableCell className="font-mono text-xs md:text-sm">{item.hof_its || '-'}</TableCell>
                  <TableCell className="font-medium text-xs md:text-sm">{item.family_name || item.hof_name}</TableCell>
                  <TableCell className="text-xs md:text-sm">{item.mobile || '-'}</TableCell>
                  <TableCell className="hidden md:table-cell text-xs md:text-sm">{item.mohalla || '-'}</TableCell>
                  <TableCell className="text-xs md:text-sm">{item.sector || '-'}</TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <Badge variant="outline" className="bg-orange-50 text-xs">{item.reason || 'Not specified'}</Badge>
                  </TableCell>
                  <TableCell>
                    {item.return_received ? (
                      <Badge className="bg-emerald-500 text-xs">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        <span className="hidden sm:inline">Collected</span>
                        <span className="sm:hidden">✓</span>
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-red-600 border-red-300 text-xs">
                        Pending
                      </Badge>
                    )}
                  </TableCell>
                  {isAdmin && (
                    <TableCell>
                      {!item.return_received && (
                        <Button 
                          size="sm" 
                          onClick={() => handleMarkComplete(item.family_id)}
                          disabled={completingReturn === item.family_id}
                          className="bg-emerald-600 hover:bg-emerald-700 text-xs px-2 py-1"
                        >
                          {completingReturn === item.family_id ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <>
                              <CheckCircle className="w-3 h-3 md:mr-1" />
                              <span className="hidden md:inline">Complete</span>
                            </>
                          )}
                        </Button>
                      )}
                      {item.return_received && (
                        <span className="text-xs text-slate-500 whitespace-nowrap">
                          By: {item.return_received_by_name || 'Admin'}
                        </span>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
